import icon from '@/assets/image/common/map_marker.png';
import { fetchGeocoder } from '@/views/StationManagement/utils/dataSearch';

export enum BMapMapType {
  NORMAL = 'BMAP_NORMAL_MAP',
  EARTH = 'BMAP_EARTH_MAP',
  SATELLITE = 'BMAP_SATELLITE_MAP',
}

const resolveMapTypeId = (type: BMapMapType) => {
  const win = window as any;
  return win[type] ?? win.BMapGL?.[type] ?? type;
};

export class TencentMap {
  public static readonly DEFAULT_DETAIL_ZOOM = 18;
  public static readonly DEFAULT_INIT_ZOOM = 14;
  private static readonly BAIDU_MAP_STYLE_ID =
    '7f6746c0a112c37f4c635c3f1a090b5c';
  public onMarked: Function = () => {};
  public container: HTMLElement | null = null;
  public map: any = null;
  public selectedMark:
    | {
        lat: any;
        lon: any;
        address: any;
        detailAddress: any;
      }
    | any = null;

  geocoder: any = null;
  infoWin: any = null;
  markers: any[] = [];
  selectedMapMaker: any = null;
  searchService: any = null;
  poiSearchCallBack: Function = async (results: any) => {
    const pois = Array.isArray(results) ? results : results?.detail?.pois || [];
    if (pois && pois.length) {
      const bounds = new window.BMapGL.Bounds(
        new window.BMapGL.Point(pois[0].location.lng, pois[0].location.lat),
        new window.BMapGL.Point(pois[0].location.lng, pois[0].location.lat),
      );
      for (let i = 0, l = pois.length; i < l; i++) {
        const poi = pois[i];
        bounds.extend(
          new window.BMapGL.Point(poi.location.lng, poi.location.lat),
        );
        this._poiSearchResult(poi, i);
      }
      this.map.setViewport(bounds);
    }
  };

  initLocation: {
    lon: number;
    lat: number;
  } = { lat: 33.95640812, lon: 118.33975212 };

  constructor({
    onMarked,
    target,
    initLocation,
  }: {
    onMarked: any;
    target: string;
    initLocation: {
      lon: number;
      lat: number;
    } | null;
  }) {
    this.onMarked = onMarked;
    this.container = document.getElementById(target);
    if (initLocation) {
      this.initLocation = initLocation;
    }
    this._initMap();
  }

  setMapCenter({
    addMarker,
    position,
    zoom,
  }: {
    addMarker?: boolean;
    position: { lon: any; lat: any };
    zoom?: number;
  }) {
    const center = new window.BMapGL.Point(position.lon, position.lat);
    if (typeof zoom === 'number') {
      this.map.centerAndZoom(center, zoom);
    } else {
      this.map.panTo(center);
      this.map.setCenter(center);
    }
    if (addMarker) {
      this.placeMarker({ position });
    } else if (this.selectedMark && this.selectedMapMaker) {
      this.selectedMapMaker.setPosition(center);
      this.selectedMark = {
        ...this.selectedMark,
        lat: position.lat,
        lon: position.lon,
      };
    }
  }

  searchPoisWithAddress({ city, address }: { city?: any; address: string }) {
    const location = city && city.length > 0 ? city : '北京';
    this._clearMarks(this.markers);
  }

  setMapType(type: BMapMapType) {
    if (!this.map || typeof this.map.setMapType !== 'function') {
      return;
    }
    if (
      type === BMapMapType.EARTH &&
      typeof this.map.isSupportEarth === 'function' &&
      !this.map.isSupportEarth()
    ) {
      return false;
    }
    this.map.setMapType(resolveMapTypeId(type));
    return true;
  }

  _clearMarks(marks: any) {
    let overlay;
    while ((overlay = this.markers.pop())) {
      this.map?.removeOverlay?.(overlay);
    }
  }

  _setSelectedMarker(marker: any) {
    this.selectedMark = marker;
    this.markers.forEach((item) => {
      if (marker === item) {
        item.setIcon(icon);
      } else {
        item.setIcon('');
      }
    });
  }

  _poiSearchResult(poi: any, index: number) {
    this.placeMarker({
      address: poi.address,
      position: {
        lat: poi.location?.lat,
        lng: poi.location?.lng,
      },
      title: poi.title,
    });
  }

  placeMarker({
    address,
    position,
    title,
  }: {
    address?: string;
    position: { lat: any; lng: any } | { lat: any; lon: any };
    title?: string;
  }) {
    const lng = 'lng' in position ? Number(position.lng) : Number(position.lon);
    const lat = Number(position.lat);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }
    this._clearMarks(this.markers);
    const point = new window.BMapGL.Point(lng, lat);
    const marker = new window.BMapGL.Marker(point, {
      enableDragging: true,
      coordType: 'BMAP_COORD_GCJ02',
      icon: new window.BMapGL.Icon(icon, new window.BMapGL.Size(32, 32)),
    });
    this.map.addOverlay(marker);
    this.markers.push(marker);
    this.selectedMapMaker = marker;
    this._updateSelectedMark({ lat, lon: lng, address: '', detailAddress: {} });
    marker.addEventListener('dragend', (e: any) => {
      const point = e.point || e.latlng;
      if (!point) {
        return;
      }
      this.handlePointSelected({ lat: point.lat, lng: point.lng });
    });
    this.resolveAddress({ address, lat, lng });
  }

  handlePointSelected(position: { lat: any; lng: any }) {
    this.placeMarker({ position });
  }

  _updateSelectedMark(marker: {
    lat: any;
    lon: any;
    address: any;
    detailAddress: any;
  }) {
    this.selectedMark = marker;
    this.onMarked ? this.onMarked(this.selectedMark) : null;
  }

  resolveAddress({
    address,
    lat,
    lng,
  }: {
    address?: string;
    lat: any;
    lng: any;
  }) {
    fetchGeocoder(`${lat},${lng}`)
      .then((res) => {
        const detailAddress = res?.detailAddress || {};
        const fullAddress = res?.address || res?.detailAddress || '';
        this._updateSelectedMark({
          lat,
          lon: lng,
          address: fullAddress,
          detailAddress,
        });
      })
      .catch((error) => {
        console.error('逆地址解析失败:', error);
        this._updateSelectedMark({
          lat,
          lon: lng,
          address: address || '',
          detailAddress: {},
        });
      });
  }

  _initMap() {
    if (!window.BMapGL || !this.container) {
      return;
    }
    window.BMapGL.coordType = 'BMAP_COORD_GCJ02';
    const center = new window.BMapGL.Point(
      this.initLocation.lon,
      this.initLocation.lat,
    );
    const qqMap = new window.BMapGL.Map(this.container, {
      coordType: 'BMAP_COORD_GCJ02',
      enableIconClick: true,
      enableDoubleClickZoom: false,
      minZoom: 5,
      maxZoom: 22,
      displayOptions: {
        building: false,
      },
    });
    qqMap.centerAndZoom(center, TencentMap.DEFAULT_INIT_ZOOM);
    qqMap.enableScrollWheelZoom();
    if (typeof qqMap.setDefaultCursor === 'function') {
      qqMap.setDefaultCursor('crosshair');
    }
    if (typeof qqMap.setDraggingCursor === 'function') {
      qqMap.setDraggingCursor('move');
    }
    if (typeof qqMap.disableDragging === 'function') {
      qqMap.disableDragging();
    }
    if (typeof qqMap.disableDoubleClickZoom === 'function') {
      qqMap.disableDoubleClickZoom();
    }
    this.map = qqMap;
    qqMap.addEventListener('click', (e: any) => {
      const point = e.latlng || e.point;
      if (!point) {
        return;
      }
      this.handlePointSelected({ lat: point.lat, lng: point.lng });
    });
    this.infoWin = new window.BMapGL.InfoWindow('', {
      width: 0,
      height: 0,
    });
    this.searchService = null;
  }
}

export const transformLocationWgs84togcj02 = (location: {
  lon: any;
  lat: any;
}): { lon: number; lat: number } => {
  const win = window as any;
  const formatedLoaction = win
    .coordtransform()
    .wgs84togcj02(parseFloat(location.lon), parseFloat(location.lat));
  return { lon: formatedLoaction[0], lat: formatedLoaction[1] };
};

export const transformLocationGcj02towgs84 = (location: {
  lon: any;
  lat: any;
}): { lon: number; lat: number } => {
  const win = window as any;
  const formatedLoaction = win
    .coordtransform()
    .gcj02towgs84(parseFloat(location.lon), parseFloat(location.lat));
  return { lon: formatedLoaction[0], lat: formatedLoaction[1] };
};
